<script setup lang="ts">
import type { UseHardwareFormData } from '~/store-v2/hardware/useHardwareValidation'
import type { AcquisitionKit, HardwareBodySave, HardwareCombosModel, HardwareCombosSelect, OptionsForm } from '~/utils/installation/Installation'
import { SolAlert, useToast } from '@solfacil/girassol'
import { toTypedSchema } from '@vee-validate/zod'
import { useField, useForm } from 'vee-validate'
import * as zod from 'zod'
import AddIcon from '~icons/material-symbols/add'
import PendingUpdateEquipmentAlert from '~/components/customer/alerts/PendingUpdateEquipmentAlert.vue'
import SolfacilSmartForm from '~/components/customer/forms/SolfacilSmartForm.vue'
import { customValidation, getHardwareListLabels, validateBatteries, validateInverters, validateModules } from '~/components/customer/forms/utils'
import CancelEquipmentEditModal from '~/components/customer/modals/CancelEquipmentEditModal.vue'
import SimulationTooltip from '~/components/simulation/Tooltip.vue'
import { useEquipmentEditPermissions } from '~/composables/equipment/useEquipmentEditPermissions'
import { usePendingUpdateEquipment } from '~/composables/equipment/usePendingUpdateEquipment'
import { useTerminalStatusLock } from '~/composables/financing/useTerminalStatusLock'
import useAuth from '~/store-v2/auth/useAuth'
import useCustomerBiometry from '~/store-v2/contract-formalization/useCustomerBiometry'
import useFinancing from '~/store-v2/financings/useFinancing'
import useHardwareForm from '~/store-v2/hardware/useHardwareForm'
import useHardwareValidation from '~/store-v2/hardware/useHardwareValidation'
import useCombosHardware from '~/store-v2/installation/useCombosHardware'
import useSaveHardware from '~/store-v2/installation/useSaveHardware'
import useProject from '~/store-v2/projects/useProject'
import useProjectContractSigned from '~/store-v2/projects/useProjectContractSigned'
import { useEquipmentEditStore } from '~/store/equipmentEdit'
import StatusDates from '~/ui-blocks/Formalization/StatusDates.vue'
import { GeneralStatuses } from '~/utils/financing-flow/Financing'
import { hasBattery, isKnownSystemType, isOnlyBattery } from '~/utils/project/systemType'
import { parseSaveError, resolveStoreOrderSaveError } from './storeOrderErrors'

const props = defineProps<{
  hardwareInformation: HardwareCombosModel
  refetchForm: () => Promise<void>
  submittedAt?: string | null
  completedAt?: string | null
  reprovedAt?: string | null
  showDateProps?: boolean | null
  showStructure?: boolean
  selectedAcquisitionKit?: AcquisitionKit
}>()

const emit = defineEmits<{
  cancelEdit: []
}>()

const { t } = useI18n()

const route = useRoute<any>()
const toast = useToast()
const projectId = computed(() => String(route.params.id))

const projectShoppingCartId: any = ref('')

const {
  createErrorToast,
  createSuccessToast,
} = useToast()
const { data: financing } = useFinancing(projectId)
const { isTerminalReadOnly } = useTerminalStatusLock(financing, 'equipment', t)
const { data: auth } = useAuth()
const { data: isContractSigned } = useProjectContractSigned(projectId)

const isContractStepAvailable = computed(() => {
  return !!financing.value?.openable_steps?.contract
})

const { data: customerBiometry } = useCustomerBiometry(projectId, isContractStepAvailable)

const equipmentEditStore = useEquipmentEditStore()

const { mutateAsync: hardwareValidation, isPending: isValidationPending } = useHardwareValidation()
const { data: project, isLoading: isProjectLoading } = useProject(projectId)
const { data: hardwareFormData } = useHardwareForm(projectId)

const { data: comboHardwareData, error: comboHardwareError } = useCombosHardware(
  projectId,
  props.hardwareInformation.system_type,
  !!(project.value?.selected_installments?.is_combo_connect),
  {
    enabled: computed(() => !isProjectLoading.value && !!project.value),
  },
)

// The previous expression ended with `&& isLoading` (a Ref, always truthy), so it never
// depended on the loading state; that no-op operand was dropped to keep the same effective rule.
const disableStepsAccordingStatus = computed(() => {
  const hardwareIsPending = financing.value?.section_statuses?.equipment?.status === GeneralStatuses.IN_PROGRESS
  return isTerminalReadOnly.value || !hardwareIsPending
})

const hasStoreOrder = computed(() => props.hardwareInformation.has_store_order)

const isStepDisabled = computed(() => Boolean(disableStepsAccordingStatus.value))
const isStoreOrder = computed(() => Boolean(hasStoreOrder.value))

const inverterSelectionFieldCount = ref()
const batterySelectionFieldCount = ref()
const loadingProjectData = ref(false)
const showButtonNext = ref(false)
const loadingBackgroundInfo = computed(() => isValidationPending.value || isProjectLoading.value)
const showCancelModal = ref(false)
const hasSolfacilSmart = ref(hardwareFormData.value?.has_solfacil_smart === true)

function setQuantityOfInvertersFields() {
  const inverters = props.hardwareInformation?.inverters
  inverterSelectionFieldCount.value = inverters && inverters.length > 0 ? inverters.length : 1
}

function setQuantityOfBatteriesFields() {
  const batteries = props.hardwareInformation?.batteries
  batterySelectionFieldCount.value = batteries && batteries.length > 0 ? batteries.length : 1
}

const radiosInverterType = [
  {
    name: 'inverterType',
    value: 'string',
    label: 'Normal (string)',
  },
  {
    name: 'inverterType',
    value: 'microinverter',
    label: 'Microinversor',
  },
]

watch(comboHardwareError, (error) => {
  if (error) {
    createErrorToast('Erro ao carregar as informações do hardware. Por favor, tente novamente.')
  }
})

function getSelectedEquipmentIds(equipmentType: string): number[] {
  const equipmentTypeMap = {
    inverters: props.hardwareInformation?.inverters?.map(item => item.id) || [],
    microinverters: props.hardwareInformation?.microinverters?.map(item => item.id) || [],
    modules: props.hardwareInformation?.modules?.map(item => item.id) || [],
    batteries: props.hardwareInformation?.batteries?.map(item => item.id) || [],
  }

  return equipmentTypeMap[equipmentType as keyof typeof equipmentTypeMap] || []
}

const optionsSelect = computed<HardwareCombosSelect>(() => {
  if (!comboHardwareData.value) {
    return {} as HardwareCombosSelect
  }

  return Object.keys(comboHardwareData.value).reduce((acc, combo) => {
    if (comboHardwareData.value?.[combo]) {
      const selectedIds = getSelectedEquipmentIds(combo)

      acc[combo] = comboHardwareData.value[combo]
        .filter((item: OptionsForm) => {
          return item.is_enabled !== false || selectedIds.includes(item.id || 0)
        })
        .map((item: OptionsForm) => ({
          name: getHardwareListLabels(item, combo),
          value: Number(item.id),
        }))
    }
    return acc
  }, {} as HardwareCombosSelect)
})

const structureOptions = computed(() =>
  (comboHardwareData.value?.structure_types ?? []).map(item => ({
    name: item.name,
    value: Number(item.id),
  })),
)

const systemPowerKwp = computed(() => {
  const simulationPower = props.hardwareInformation?.simulation_system_power
  if (simulationPower != null && !Number.isNaN(Number(simulationPower)))
    return Number(simulationPower)
  return project.value?.system_power ?? 0
})

// only_battery: the form shows, validates and sends batteries only (no modules, inverters, phase or structure).
const isOnlyBatterySystem = computed(() => isOnlyBattery(props.hardwareInformation?.system_type))
const hasBatterySystem = computed(() => hasBattery(props.hardwareInformation?.system_type))

const systemTypeOption = computed(() => {
  const isBattery = props.hardwareInformation?.system_type === 'with_battery'
  let name = isBattery
    ? t('customer.equipment_step.project_details.system_type_with_battery')
    : t('customer.equipment_step.project_details.system_type_without_battery')

  if (isOnlyBatterySystem.value)
    name = t('customer.equipment_step.project_details.system_type_only_battery')

  // A system type the front doesn't know (new backend value) must not be shown as "Sem bateria".
  const systemType = props.hardwareInformation?.system_type
  if (systemType && !isKnownSystemType(systemType))
    name = '-'

  return {
    name,
    value: props.hardwareInformation?.system_type ?? 'without_battery',
  }
})

const inverterTypeSelect = ref('')

const isMicroinversor = ref((props.hardwareInformation?.microinverters?.length ?? 0) > 0)

function getInverterBrand(label?: string) {
  if (!label) {
    return ''
  }
  return label.split('-')[0]?.trim().toLowerCase() ?? ''
}

function isValidInverterOption(item?: { name?: string, value?: string }) {
  if (!item) {
    return false
  }
  return String(item.value) !== '0' && item.name !== 'Selecione'
}

const validationSchema = computed(() => toTypedSchema(
  zod.object({
    structure: props.showStructure && !isOnlyBatterySystem.value
      ? zod.object({
          name: zod.string().min(1, t('form.required')),
          value: zod.number().min(1, t('form.required')),
        })
      : zod.any().optional(),
    installationPhase: isOnlyBatterySystem.value
      ? zod.any().optional()
      : zod.object({
          name: zod.string().min(1, t('form.required')),
          value: zod.number().min(1, t('form.required')),
        }),
    inverterType: isOnlyBatterySystem.value
      ? zod.any().optional()
      : zod.enum(['string', 'microinverter']),
    microinversorQuantity: zod.any().optional(),
    inverters: (isOnlyBatterySystem.value || inverterTypeSelect.value === 'microinverter')
      ? zod.any().optional()
      : zod.array(zod.object({
          name: zod.string({ required_error: t('form.required') }).min(1, t('form.required')),
          value: zod.string({ required_error: t('form.required') }).min(1, t('form.required')).min(1, t('form.required')),
          quantity: zod.number({ required_error: t('form.required') }).min(1, t('form.required')),
        }, { required_error: t('form.required') })),
    batteries: hasBatterySystem.value
      ? zod.array(zod.object({
          name: zod.string({ required_error: t('form.required') }).min(1, t('form.required')),
          value: zod.string({ required_error: t('form.required') }).min(1, t('form.required')),
          quantity: zod.number({ required_error: t('form.required') }).min(1, t('form.required')),
        }))
      : zod.any().optional(),
    microinversors: !isOnlyBatterySystem.value && inverterTypeSelect.value === 'microinverter'
      ? zod.array(zod.object({
          name: zod.string({ required_error: t('form.required') }).min(1, t('form.required')),
          value: zod.string({ required_error: t('form.required') }).min(1, t('form.required')),
          quantity: zod.number({ required_error: t('form.required') }).min(1, t('form.required')),
        }))
      : zod.any().optional(),
    modules: zod.object({
      name: zod.string({ required_error: t('form.required') }).min(1, t('form.required')),
      value: zod.string({ required_error: t('form.required') }).min(1, t('form.required')),
      quantity: zod.number({ required_error: t('form.required') }).min(1, t('form.required')),
    }).optional(),
  }),
))

function getInitialValues() {
  return {
    structure: {
      name: props?.hardwareInformation?.structure?.name || 'Selecione',
      value: props?.hardwareInformation?.structure?.id || 0,
    },
    installationPhase: {
      name: props?.hardwareInformation?.phase?.name || 'Selecione',
      value: props?.hardwareInformation?.phase?.id || 0,
    },
    inverterType: props?.hardwareInformation?.inverter_type || 'string',
    inverters: props?.hardwareInformation?.inverters !== null && props?.hardwareInformation?.inverters?.length > 0
      ? props.hardwareInformation.inverters.map((item) => { return { name: getHardwareListLabels(item as unknown as OptionsForm, 'inverters'), value: String(item.id), quantity: item.quantity } })
      : [],
    batteries: props?.hardwareInformation?.batteries !== null && props?.hardwareInformation?.batteries?.length > 0
      ? props.hardwareInformation.batteries.map((item) => { return { name: getHardwareListLabels(item as unknown as OptionsForm, 'batteries'), value: String(item.id), quantity: item.quantity } })
      : [],
    microinversors: props?.hardwareInformation?.microinverters !== null && (props?.hardwareInformation?.microinverters?.length ?? 0) > 0
      ? props.hardwareInformation.microinverters.map((item) => { return { name: getHardwareListLabels(item as unknown as OptionsForm, 'microinverters'), value: String(item.id), quantity: item.quantity } })
      : [],
    modules: props?.hardwareInformation?.modules !== null && props?.hardwareInformation?.modules?.length > 0
      ? props.hardwareInformation.modules.map((item) => { return { name: getHardwareListLabels(item as unknown as OptionsForm, 'modules'), value: String(item.id), quantity: item.quantity } }).reduce((acc, cur) => Object.assign(acc, cur))
      : undefined,
  }
}

watch(hardwareFormData, (data) => {
  if (data) {
    hasSolfacilSmart.value = data.has_solfacil_smart === true
  }
}, { immediate: true })

const { values, validate, setFieldValue, errors, setErrors, setFieldError, resetForm } = useForm({
  validationSchema,
  initialValues: getInitialValues(),
  validateOnMount: false,
})

const inverterBrandErrorMessage = t('project_data.errors.inverter_brand_mismatch')

function clearInverterBrandError(index: number) {
  const fieldPath = `inverters[${index}]`
  if (errors.value[fieldPath] === inverterBrandErrorMessage) {
    setFieldError(fieldPath as any, '')
  }
}

function validateInverterBrandMatch(selectedInverters?: Array<{ name?: string, value?: string }>) {
  const inverters = selectedInverters ?? values.inverters ?? []
  const firstInverter = inverters[0]

  if (!isValidInverterOption(firstInverter)) {
    inverters.forEach((_, index) => {
      clearInverterBrandError(index)
    })
    return
  }

  const firstBrand = getInverterBrand(firstInverter?.name)
  inverters.forEach((item, index) => {
    if (index === 0) {
      clearInverterBrandError(index)
      return
    }

    if (!isValidInverterOption(item) || !firstBrand) {
      clearInverterBrandError(index)
      return
    }

    const currentBrand = getInverterBrand(item?.name)
    if (currentBrand && currentBrand !== firstBrand) {
      setFieldError(`inverters[${index}]` as any, inverterBrandErrorMessage)
      return
    }

    clearInverterBrandError(index)
  })
}

const isFormValid = computed(() => {
  const errorKeys = Object.keys(errors.value)

  if (errorKeys.length === 0) {
    return true
  }

  return errorKeys.filter(key => key.match(/(^.[a-z]+)$/)).length === 0
})

const hasInverterBrandError = computed(() => {
  return Object.keys(errors.value).some(key => key.startsWith('inverters['))
})

watch(values, (newValue) => {
  inverterTypeSelect.value = newValue.inverterType ?? ''
})

function showErrorStringInverter(isUnderExpected: boolean) {
  if (isUnderExpected) {
    values.inverters?.forEach((_, index) => {
      setErrors({
        ...errors.value,
        [`inverters[${index}]`]: t('project_data.errors.string_overload_min'),
      })
    })

    return
  }

  values.inverters?.forEach((_, index) => {
    setErrors({
      ...errors.value,
      [`inverters[${index}]`]: t('project_data.errors.string_overload_max'),
    })
  })
}

function showErrorMicroinverter(isUnderExpected: boolean) {
  if (isUnderExpected) {
    values.microinversors?.forEach((_, index) => {
      setErrors({
        ...errors.value,
        [`microinversors[${index}]`]: t('project_data.errors.microinverter_overload_min'),
      })
    })

    return
  }

  values.microinversors?.forEach((_, index) => {
    setErrors({
      ...errors.value,
      [`microinversors[${index}]`]: t('project_data.errors.microinverter_overload_max'),
    })
  })
}

function showPricePerWattError(validation: UseHardwareFormData) {
  if (validation?.hardware?.modules?.price_per_watt?.is_under_expected) {
    setErrors({
      ...errors.value,
      'modules.value': t('project_data.errors.modules_price_per_watt_under'),
    })
    return
  }

  if (validation?.hardware?.modules?.price_per_watt?.is_over_expected) {
    setErrors({
      ...errors.value,
      'modules.value': t('project_data.errors.modules_price_per_watt_over'),
    })
  }
}

function showBatteryCapacityError(validation: UseHardwareFormData) {
  const capacity = validation?.hardware?.batteries?.capacity
  let message = t('project_data.errors.battery_capacity')

  if (capacity?.is_under_expected)
    message = t('project_data.errors.battery_capacity_under')
  else if (capacity?.is_over_expected)
    message = t('project_data.errors.battery_capacity_over')

  setErrors({
    ...errors.value,
    'batteries[0].value': message,
  })
}

// Adicionar/remover linha não passa por handleEquipmentChange; limpa só o erro de capacidade.
function clearBatteryCapacityError() {
  const capacityMessages = ['battery_capacity', 'battery_capacity_under', 'battery_capacity_over']
    .map(key => t(`project_data.errors.${key}`))

  if (isOnlyBatterySystem.value && capacityMessages.includes(errors.value['batteries[0].value'] ?? '')) {
    setFieldError('batteries[0].value' as any, '')
  }
}

async function validateHardwareForm(form: HardwareBodySave) {
  const validation = await hardwareValidation(form)
  let hasBatteryCapacityError = false

  if (!validation) {
    return { isValid: false, hasBatteryCapacityError }
  }

  if (validation.is_valid) {
    Object.keys(errors.value).forEach((key) => {
      if (key.startsWith(`inverters`)) {
        setFieldError(key as any, '')
      }
    })
    setErrors({})
  }
  else {
    if (validation?.hardware?.modules?.system_power?.is_valid === false) {
      setErrors({
        ...errors.value,
        'modules.value': t('project_data.errors.modules_system_power'),
      })
    }
    else if (validation.hardware?.modules?.price_per_watt?.is_valid === false) {
      showPricePerWattError(validation)
    }

    if (validation.hardware?.inverters?.overload?.is_valid === false) {
      const { is_under_expected: _isUnderExpected } = validation.hardware.inverters.overload
      const isUnderExpected = Boolean(_isUnderExpected)

      if (values.inverterType === 'string') {
        showErrorStringInverter(isUnderExpected)
      }
      else {
        showErrorMicroinverter(isUnderExpected)
      }
    }

    if (isOnlyBatterySystem.value && validation.hardware?.batteries?.capacity?.is_valid === false) {
      showBatteryCapacityError(validation)
      hasBatteryCapacityError = true
    }
  }

  return { isValid: validation.is_valid, hasBatteryCapacityError }
}

function addInverter() {
  if (isTerminalReadOnly.value)
    return

  setFieldValue('inverters', [...values.inverters, { name: 'Selecione', value: '0', quantity: 0 }])
  inverterSelectionFieldCount.value = inverterSelectionFieldCount.value + 1
}

function addBattery() {
  if (isTerminalReadOnly.value)
    return

  setFieldValue('batteries', [...values.batteries, { name: 'Selecione', value: '0', quantity: 0 }])
  batterySelectionFieldCount.value = values?.batteries?.length
  clearBatteryCapacityError()
}

const invertersKey = ref(0)
const batteriesKey = ref(0)
const modulesKey = ref(0)

function removeInverter(index: number) {
  if (isTerminalReadOnly.value)
    return

  const inverterToRemove = values.inverters.filter((_, currentIndex) => currentIndex !== index)

  setFieldValue('inverters', inverterToRemove)

  Object.keys(errors.value).forEach((key) => {
    if (key.startsWith(`inverters[${index}]`)) {
      setFieldError(key as any, '')
    }
  })

  invertersKey.value = invertersKey.value + 1
  inverterSelectionFieldCount.value = inverterSelectionFieldCount.value - 1
  validateInverterBrandMatch(inverterToRemove)
}

function removeBattery(index: number) {
  if (isTerminalReadOnly.value)
    return

  const valueToRemove = values.batteries[index]
  const batteryToRemove = values.batteries.filter(item => item !== valueToRemove)
  setFieldValue('batteries', batteryToRemove)

  Object.keys(errors.value).forEach((key) => {
    if (key.startsWith(`batteries[${index}]`)) {
      setFieldError(key as any, '')
    }
  })
  clearBatteryCapacityError()

  batteriesKey.value = batteriesKey.value + 1
  batterySelectionFieldCount.value = values?.batteries?.length
}

function resetField() {
  inverterSelectionFieldCount.value = 1
  batterySelectionFieldCount.value = 1

  if (props.hardwareInformation?.phase !== null && props.hardwareInformation.phase.id === 0)
    setFieldValue('installationPhase', { name: 'Selecione', value: 0 })

  if (values.inverterType === 'microinverter') {
    isMicroinversor.value = true
    setFieldValue('inverters', [])
    setFieldValue('microinversors', [])
    inverterSelectionFieldCount.value = 1

    Object.keys(errors.value).forEach((key) => {
      if (key.startsWith('inverters[')) {
        setFieldError(key as any, '')
      }
    })
  }
  else {
    isMicroinversor.value = false
    setFieldValue('inverters', [])
  }

  if (hasBatterySystem.value) {
    setFieldValue('batteries', [])
  }
}

watch(projectShoppingCartId, (newValue) => {
  if (newValue)
    projectShoppingCartId.value = newValue
})

function getHardwareFormPayload() {
  const invertersPayload
    = isMicroinversor.value
      ? values.microinversors?.map(
          (item) => {
            return {
              name: item.name,
              id: Number(item.value),
              quantity: item.quantity,
            }
          },
        )
      : values.inverters?.map((item) => {
          return {
            name: item.name,
            id: Number(item.value),
            quantity: item.quantity,
          }
        })

  const batteriesPayload
    = hasBatterySystem.value
      ? values.batteries?.map((item) => {
          return {
            name: item.name,
            id: Number(item.value),
            quantity: item.quantity,
          }
        })
      : []

  const basePayload = {
    is_combo: props.hardwareInformation.is_combo,
    project_id: projectId.value,
    distributor_id: props.hardwareInformation?.distributor?.id ?? 0,
  }
  const acquisitionKitPayload = props.selectedAcquisitionKit ? { selected_aquisition_kit: props.selectedAcquisitionKit } : {}

  if (isOnlyBatterySystem.value) {
    return {
      ...basePayload,
      batteries: batteriesPayload,
      has_solfacil_smart: hasSolfacilSmart.value,
      ...acquisitionKitPayload,
    } satisfies HardwareBodySave
  }

  const hardwareDataForm: HardwareBodySave = {
    ...basePayload,
    phase_id: values?.installationPhase?.value !== undefined ? values?.installationPhase?.value : 0,
    inverters: invertersPayload,
    modules: [{ id: Number(values.modules?.value) ?? 0, quantity: Number(values.modules?.quantity) }],
    inverter_type: values.inverterType,
    batteries: batteriesPayload,
    has_solfacil_smart: hasSolfacilSmart.value,
    ...(props.showStructure && Number(values?.structure?.value) ? { structure_id: Number(values.structure.value) } : {}),
    ...acquisitionKitPayload,
  }

  return hardwareDataForm
}

const { mutateAsync: mutateSaveHardware } = useSaveHardware(projectId)

const isEditingEquipment = computed(() => equipmentEditStore.isProjectBeingEdited(projectId.value))

const { hasPendingTAF } = usePendingUpdateEquipment(projectId)

const {
  isAdmin: _isAdmin,
  isEditButtonDisabled,
  editButtonTooltipText,
  showContractUpdateEquipmentAlert,
} = useEquipmentEditPermissions({
  financing,
  customerBiometry,
  isContractSigned,
  auth,
  isEditingEquipment,
  hasPendingTAF,
})

const shouldDisableFields = computed(() => {
  // Terminal financing (expired / reproved / canceled) wins over the persisted edit mode.
  if (isTerminalReadOnly.value) {
    return true
  }
  if (isEditingEquipment.value) {
    return false
  }
  return isStepDisabled.value
})

const shouldDisableBasicFields = computed(() => {
  return shouldDisableFields.value || isStoreOrder.value || isEditingEquipment.value
})

async function saveProject() {
  if (isTerminalReadOnly.value)
    return

  const { valid } = await validate()

  if (valid) {
    try {
      loadingProjectData.value = true

      const hardwareDataForm = getHardwareFormPayload()

      if (isEditingEquipment.value) {
        await mutateSaveHardware(hardwareDataForm)
        equipmentEditStore.stopEditingEquipment()
        toast.createSuccessToast({
          title: t('customer.equipment_update.toast.update_success_title'),
          description: t('customer.equipment_update.toast.update_success_without_taf_description'),
        })

        await reloadEquipmentFields()
      }
      else {
        await mutateSaveHardware(hardwareDataForm)
        createSuccessToast(t('customer.equipment_success_save'))
      }

      showButtonNext.value = true
    }
    catch (error: any) {
      loadingProjectData.value = false
      const result = resolveStoreOrderSaveError(error)
      if (result.kind === 'select' || result.kind === 'field-toast')
        return createErrorToast(t(result.messageKey))
      if (result.kind === 'alert')
        return createErrorToast({ title: t(result.titleKey), description: result.message })

      const { message } = parseSaveError(error)
      return createErrorToast(message || t('customer.equipment_error_save'))
    }
    finally {
      loadingProjectData.value = false
    }
  }
  else {
    createErrorToast(t('form.alert_required_fields'))
  }
}

function validateMicroinverters() {
  const micro = values.microinversors?.[0]
  let hasError = false

  if (!micro?.value || micro.value === '0') {
    setFieldError('microinversors[0].value' as any, t('form.required'))
    hasError = true
  }
  else {
    setFieldError('microinversors[0].value' as any, '')
  }

  if (!micro?.quantity) {
    setFieldError('microinversors[0].quantity' as any, t('form.required'))
    hasError = true
  }
  else {
    setFieldError('microinversors[0].quantity' as any, '')
  }

  return hasError
}

async function submitTechnicalData() {
  if (isTerminalReadOnly.value)
    return

  const { valid } = await validate()
  const isMicro = !isOnlyBatterySystem.value && inverterTypeSelect.value === 'microinverter'
  let hasError = isOnlyBatterySystem.value
    ? validateBatteries(values, errors, setErrors, t)
    : customValidation(
        values,
        errors,
        setErrors,
        t,
        props.hardwareInformation.system_type === 'with_battery',
        isMicro,
      )

  if (isMicro)
    hasError = validateMicroinverters() || hasError

  if (!valid || hasError) {
    return
  }

  const hardwareDataForm = getHardwareFormPayload()
  const isValid = isOnlyBatterySystem.value
    ? await validateOnlyBatteryHardware(hardwareDataForm)
    : (await validateHardwareForm(hardwareDataForm)).isValid

  if (isValid)
    await saveProject()
}

// No só-bateria os cards de módulo/inversor (onde a pré-validação grava os erros) não são
// renderizados: reprovação ou falha da pré-validação precisam virar toast, exceto
// a reprovação por capacidade, que já aparece no campo da bateria.
async function validateOnlyBatteryHardware(form: HardwareBodySave) {
  try {
    const { isValid, hasBatteryCapacityError } = await validateHardwareForm(form)

    if (!isValid && !hasBatteryCapacityError)
      createErrorToast(t('customer.equipment_error_save'))

    return isValid
  }
  catch {
    createErrorToast(t('customer.equipment_error_save'))
    return false
  }
}

function handleEditEquipment() {
  if (isTerminalReadOnly.value)
    return

  equipmentEditStore.startEditingEquipment(projectId.value)
}

function handleCancelClick() {
  if (isTerminalReadOnly.value)
    return

  showCancelModal.value = true
}

async function reloadEquipmentFields() {
  const initialValues = getInitialValues()

  inverterTypeSelect.value = initialValues.inverterType
  isMicroinversor.value = initialValues.inverterType === 'microinverter'

  inverterSelectionFieldCount.value = initialValues.inverters?.length || 1
  batterySelectionFieldCount.value = initialValues.batteries?.length || 1

  await nextTick()

  resetForm({
    values: initialValues,
  })

  await nextTick()

  modulesKey.value++
  invertersKey.value++
  batteriesKey.value++

  await nextTick()
}

async function confirmCancelEdit() {
  if (isTerminalReadOnly.value)
    return

  showCancelModal.value = false

  await reloadEquipmentFields()
  emit('cancelEdit')
}

watch(isTerminalReadOnly, (isReadOnly) => {
  if (!isReadOnly)
    return

  showCancelModal.value = false
  reloadEquipmentFields()
})

watch(isEditingEquipment, (isEditing, wasEditing) => {
  if (wasEditing && !isEditing && isTerminalReadOnly.value)
    reloadEquipmentFields()
})

function handleModuleChange(value: { name?: string, value?: string, quantity?: number }) {
  setFieldValue('modules', value)

  if (errors.value['modules.value']) {
    setFieldError('modules.value', '')
  }

  Object.keys(errors.value).forEach((errorKey) => {
    if (errorKey.startsWith('inverters[') || errorKey.startsWith('microinversors[')) {
      setFieldError(errorKey as any, '')
    }
  })

  if (errors.value.microinversorQuantity) {
    setFieldError('microinversorQuantity', '')
  }

  validateModules(values, errors, setErrors, t)

  validateInverterBrandMatch(values.inverters as Array<{ name?: string, value?: string }>)
}

function handleEquipmentChange(index: number, key: keyof typeof values, value: { name?: string, value?: string, quantity?: number }) {
  const equipment = [...values[key]]
  equipment[index] = value

  setFieldValue(key, equipment)

  if (key === 'inverters') {
    validateInverterBrandMatch(equipment as Array<{ name?: string, value?: string }>)
  }

  if (errors.value['modules.value']) {
    setFieldError('modules.value', '')
  }

  Object.keys(errors.value).forEach((errorKey) => {
    if (errorKey.startsWith('microinversors[')) {
      setFieldError(errorKey as any, '')
    }
    if (key !== 'inverters' && errorKey.startsWith('inverters[')) {
      setFieldError(errorKey as any, '')
    }
  })

  if (errors.value.microinversorQuantity) {
    setFieldError('microinversorQuantity', '')
  }

  if (key === 'inverters') {
    validateInverters(values, errors, setErrors, t)
  }

  if (key === 'microinversors') {
    validateMicroinverters()
  }

  if (hasBatterySystem.value && key === 'batteries') {
    validateBatteries(values, errors, setErrors, t)
  }
}

onMounted(async () => {
  setQuantityOfInvertersFields()
  setQuantityOfBatteriesFields()
})
</script>

<template>
  <div class="form-project-data">
    <SolAlert
      v-if="showContractUpdateEquipmentAlert"
      id="contract-update-equipment-alert"
      class="mb-2xs"
      feedback="informative"
    >
      <p class="font-bold">
        {{ t('customer.equipment_update.alert.contract_generated_title') }}
      </p>
      <p class="mt-2">
        {{ t('customer.equipment_update.alert.contract_generated_description') }}
      </p>
    </SolAlert>

    <PendingUpdateEquipmentAlert
      :title="t('customer.equipment_update.alert.pending_title')"
      :description="t('customer.equipment_update.alert.pending_description')"
      :read-only="isTerminalReadOnly"
    />

    <form id="hardware-form">
      <div class="p-6 rounded-lg bg-neutral-high-light mb-6">
        <div class="grid grid-cols-1 md:system:grid-cols-4 gap-6 items-start">
          <SolSelect
            v-if="!isOnlyBatterySystem"
            id="installation-phase-field"
            name="installationPhase"
            :label="t('customer.equipment_step.project_details.phase')"
            :use-field="useField"
            :options="optionsSelect.phase"
            :disabled="shouldDisableFields || isStoreOrder"
          />

          <SolSelect
            v-if="showStructure && !isOnlyBatterySystem"
            id="structure-field"
            name="structure"
            :label="t('customer.equipment_step.project_details.structure')"
            :use-field="useField"
            :options="structureOptions"
            :searchable="true"
            :placeholder="t('customer.equipment_step.project_details.structure_placeholder')"
            :disabled="shouldDisableFields || isStoreOrder"
          />

          <SolInputText
            id="system-power-field"
            name="system_power_display"
            :model-value="systemPowerKwp.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })"
            :label="isOnlyBatterySystem ? t('customer.equipment_step.project_details.battery_capacity') : t('customer.equipment_step.project_details.system_power')"
            :disabled="true"
          >
            <template #icon:right>
              <span class="fonts-body-medium-regular text-neutral-low-light">{{ isOnlyBatterySystem ? 'kWh' : 'kWp' }}</span>
            </template>
          </SolInputText>

          <SolSelect
            id="system-type-field"
            name="system_type_display"
            :label="t('customer.equipment_step.project_details.system_type')"
            :options="[systemTypeOption]"
            :selected="systemTypeOption"
            :disabled="true"
          />
        </div>
      </div>

      <!-- Módulos -->
      <div v-if="!isOnlyBatterySystem" class="p-6 rounded-lg bg-neutral-high-light mb-6">
        <div class="grid grid-cols-1 md:system:grid-cols-[1fr_auto] gap-6 items-start">
          <EquipmentSelect
            id="inverter-field-module"
            :key="`module-${modulesKey}`"
            v-model="values.modules as any"
            :select-label="t('form.modules')"
            :counter-label="t('form.quantity')"
            :select-error="(!Number(values.modules?.value) || values.modules?.value === '0') && errors.modules || errors['modules.value']"
            :counter-error="errors['modules.quantity']"
            :options="optionsSelect.modules"
            :disabled="Boolean(shouldDisableFields || isStoreOrder)"
            :equipments="optionsSelect.modules"
            :removable="false"
            data-testid="module-quantity-input"
            @update:model-value="handleModuleChange($event)"
          />
        </div>
      </div>

      <!-- Inversor -->
      <div v-if="!isOnlyBatterySystem" class="p-6 rounded-lg bg-neutral-high-light mb-6">
        <template v-if="props.hardwareInformation.system_type === 'without_battery'">
          <label
            :class="{ 'pointer-events-none opacity-60': shouldDisableFields }"
            for="inverter-type"
            class="fonts-body-medium-bold text-neutral-low-dark"
          >
            {{ t('project_data.form.inverter_type') }}
          </label>
          <div class="grid grid-cols-1 md:system:grid-cols-2 gap-6 mb-6">
            <SolRadioGroup
              id="inverter-type"
              class="mb-2"
              title=""
              name="inverterType"
              :use-field="useField"
              direction="row"
              :radios="radiosInverterType"
              :disabled="shouldDisableFields || isStoreOrder"
              @change="resetField()"
            />
          </div>
        </template>

        <template v-if="values.inverterType === 'microinverter'">
          <div class="grid grid-cols-1 gap-6 mb-6">
            <div class="grid grid-cols-1 md:system:grid-cols-[1fr_auto] gap-6 items-start">
              <EquipmentSelect
                id="microinversor-field-0"
                v-model="values.microinversors[0]"
                :select-label="`${t('form.microinversors')}`"
                :counter-label="t('form.quantity')"
                :select-error="(!Number(values.microinversors?.[0]?.value) || values.microinversors[0]?.value === '0') && errors.microinversors || errors['microinversors[0]'] || errors['microinversors[0].value']"
                :counter-error="errors['microinversors[0].quantity']"
                :options="optionsSelect.microinverters"
                :removable="false"
                :disabled="shouldDisableFields || isStoreOrder"
                :equipments="optionsSelect.microinverters"
                data-test-id="microinversor-quantity-0"
                @update:model-value="handleEquipmentChange(0, 'microinversors', $event)"
              />
            </div>
          </div>
        </template>

        <template v-else>
          <div class="mb-6">
            <div class="grid grid-cols-1 gap-6 mb-6">
              <div
                v-for="(_, index) in inverterSelectionFieldCount"
                :key="`${index}-${invertersKey}`"
                class="grid grid-cols-1 md:system:grid-cols-[1fr_auto] gap-6 items-start"
              >
                <EquipmentSelect
                  :id="`inverter-field-${index}`"
                  v-model="values.inverters[index]"
                  :select-label="`${t('form.inverter')}`"
                  :counter-label="t('form.quantity')"
                  :select-error="(!Number(values.inverters?.[index]?.value) || values.inverters[index]?.value === '0') && errors.inverters || errors[`inverters[${index}]`] || errors[`inverters[${index}].value`]"
                  :counter-error="errors[`inverters[${index}].quantity`]"
                  :options="optionsSelect.inverters"
                  :removable="inverterSelectionFieldCount > 1"
                  :disabled="shouldDisableFields || isStoreOrder"
                  :equipments="optionsSelect.inverters"
                  :data-test-id="`inverter-quantity-${index}`"
                  @remove="removeInverter(index)"
                  @update:model-value="handleEquipmentChange(index, 'inverters', $event)"
                />
              </div>
            </div>

            <SolButton
              id="addInverter"
              size="large"
              variant="secondary"
              :disabled="values.inverters.length === 0 || values?.inverters?.some((item) => [String(item.value), String(item.quantity)].includes('0')) || shouldDisableFields || isStoreOrder"
              @click="addInverter()"
            >
              <template #icon:left>
                <AddIcon />
              </template>
              {{ t('form.button.add_inverter') }}
            </SolButton>
          </div>
        </template>
      </div>

      <!-- Bateria -->
      <template v-if="hasBatterySystem">
        <div class="p-6 rounded-lg bg-neutral-high-light mb-6">
          <div class="grid grid-cols-1 gap-6 mb-6">
            <div
              v-for="(_, index) in batterySelectionFieldCount"
              :key="`${index}-${batteriesKey}`"
              class="grid grid-cols-1 md:system:grid-cols-[1fr_auto] gap-6 items-start"
            >
              <EquipmentSelect
                :id="`battery-field-${index}`"
                v-model="values.batteries[index]"
                :select-label="`${t('form.battery')}`"
                :counter-label="t('form.quantity')"
                :options="optionsSelect.batteries"
                :removable="batterySelectionFieldCount > 1"
                :select-error="(!Number(values.batteries?.[index]?.value) || values.batteries[index]?.value === '0') && errors.batteries || errors[`batteries[${index}].value`]"
                :counter-error="errors[`batteries[${index}].quantity`]"
                :disabled="shouldDisableFields || isStoreOrder"
                :equipments="optionsSelect.batteries"
                :data-test-id="`battery-quantity-${index}`"
                @remove="removeBattery(index)"
                @update:model-value="handleEquipmentChange(index, 'batteries', $event)"
              />
            </div>
          </div>

          <SolButton
            id="addBattery"
            size="large"
            variant="secondary"
            :disabled="values?.batteries?.length === 0 || values?.batteries?.some((item) => [String(item.value), String(item.quantity)].includes('0')) || shouldDisableFields || isStoreOrder"
            @click="addBattery()"
          >
            <template #icon:left>
              <AddIcon />
            </template>
            {{ t('form.button.add_battery') }}
          </SolButton>
        </div>
      </template>

      <SolfacilSmartForm
        v-model="hasSolfacilSmart"
        :project-id="projectId"
        :disabled="shouldDisableBasicFields"
        :read-only="isTerminalReadOnly"
      />

      <div class="grid grid-cols-1 gap-6 mt-6">
        <SolDivider thickness="x-small" orientation="horizontal" />
      </div>
      <div class="grid grid-cols-1 gap-6 mt-6">
        <div :class="`flex ${showDateProps ? 'justify-between' : 'justify-end'} w-full gap-3 sm:system:items-center flex-col sm:system:flex-row w-full`">
          <StatusDates
            v-if="showDateProps"
            :submitted-at="submittedAt"
            :completed-at="completedAt"
            :reproved-at="reprovedAt"
          />
          <div class="md:system:col-start-4 flex justify-end gap-3">
            <SimulationTooltip
              v-if="isStepDisabled && !isEditingEquipment && isEditButtonDisabled && editButtonTooltipText"
              :text="editButtonTooltipText"
              position="top"
              variant="default"
            >
              <SolButton
                id="edit_equipments"
                size="large"
                variant="secondary"
                class="w-full md:system:w-auto"
                :disabled="isEditButtonDisabled"
                @click="handleEditEquipment"
              >
                {{ t('app.edit_equipment') }}
              </SolButton>
            </SimulationTooltip>
            <SolButton
              v-if="isStepDisabled && !isEditingEquipment && (!isEditButtonDisabled || !editButtonTooltipText)"
              id="edit_equipments"
              size="large"
              variant="secondary"
              class="w-full md:system:w-auto"
              :disabled="isEditButtonDisabled"
              @click="handleEditEquipment"
            >
              {{ t('app.edit_equipment') }}
            </SolButton>
            <SolButton
              v-if="isEditingEquipment"
              id="cancel_edit_equipments"
              size="large"
              variant="secondary"
              class="w-full md:system:w-auto"
              :disabled="isTerminalReadOnly"
              @click="handleCancelClick"
            >
              {{ t('app.cancel_equipment_edit') }}
            </SolButton>
            <SolButton
              id="save_equipments"
              size="large"
              class="w-full md:system:w-auto"
              :loading="loadingProjectData || loadingBackgroundInfo"
              :disabled="shouldDisableFields || !isFormValid || hasInverterBrandError || isStoreOrder"
              @click="submitTechnicalData"
            >
              {{ isEditingEquipment ? t('app.save_equipment_changes') : t('customer.equipment_step.actions.save_continue') }}
            </SolButton>
          </div>
        </div>
      </div>
    </form>

    <CancelEquipmentEditModal
      :is-open="showCancelModal"
      @close="showCancelModal = false"
      @confirm="confirmCancelEdit"
      @cancel="showCancelModal = false"
    />
  </div>
</template>

<style lang="scss" scoped>
#hardware-form {
  :deep(.sol-alert-core.-informative) {
    @apply block mb-2xs;

    @screen md:system {
      @apply flex;
    }

    .sol-button-core.-secondary {
      @apply ml-sm mt-micro min-w-max;

      @screen md:system {
        @apply ml-0 mt-0;
      }
    }
  }
}

.form-project-data{
  @apply mb-0;
}
</style>
